import { UseFormReturn } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import i18next from "i18next";
import toast from "react-hot-toast";
import { AuthService } from "../../services/auth.service";
import {
  IAuth,
  mixpanelProfilePayload,
  useAuthStore,
} from "../../store/session";
import showErrorToast, { ErrorToastConfig } from "../../components/Error";
import { IBusinessResponse } from "../../types/business";
import { BusinessService } from "../../services/business.service";
import { TrackerService } from "../../services/tracker.service";
import { TRACKER_CONSTANTS } from "../../constants/tracker.events";
import { RecaptchaVerifier } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CatalogType } from "../../types/catalog";
import { PaymentFlowTypeEnum } from "../../types/order";

type FormMethods = Pick<
  UseFormReturn<any>,
  "control" | "watch" | "setValue" | "getValues" | "setError"
>;

const RESEND_CODE_COUNTDOWN = 10 * 3;

const useFormContainer = (formMethods: FormMethods, catalog?: CatalogType) => {
  const { getValues, setValue } = formMethods;

  const { login, session } = useAuthStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(session ? 3 : 1);
  const [isDisabled, setIsDisabled] = useState(false);
  const [changePhoneSeconds, setChangePhoneSeconds] = useState(
    RESEND_CODE_COUNTDOWN
  );
  const [phoneNoChangeAttempts, setPhoneNoChangeAttempts] = useState(
    parseInt(localStorage.getItem("phoneNoChangeAttemp") || "3")
  );
  const [otpInput, setOtpInput] = useState("");
  const [maxTries, setMaxTries] = useState(3);
  const [seconds, setSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [showPhoneCompleted, setShowPhoneCompleted] = useState(false);
  const [showPhone, setShowPhone] = useState(true);
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [showShippingAddress, setShowShippingAddress] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [phoneSigninPayload, setphoneSigninPayload] = useState({
    phoneNumber: "",
    phoneNumberCountryCode: "",
  });
  const [openBusinessSelectionModal, setOpenBusinessSelectionModal] =
    useState(false);

  const timerRef = useRef<NodeJS.Timeout | undefined>();
  // const changePhoneTimerRef = useRef<NodeJS.Timeout | undefined>();

  /**
   * mutations and queries
   */

  const otpVerifyMutation = useMutation({
    mutationFn: handleSignUpWithPhone,
    onSuccess: (data) => {
      if (data) signinSSOMutation.mutate({ accessToken: data });
      else {
        toast(
          showErrorToast(i18next.t(`commons.error.form.invalidOtp`)),
          ErrorToastConfig
        );
      }
      setIsLoading(false);
    },
    onError: (error: any) => {
      toast(
        showErrorToast(
          i18next.t(
            `commons.error.${
              error.response.data.message || "Something went wrong."
            }`
          )
        ),
        ErrorToastConfig
      );
      setIsDisabled(false);
      setIsLoading(false);
    },
  });

  const signinSSOMutation = useMutation({
    mutationFn: AuthService.signinSSO,
    onSuccess: ({ data }) => {
      if (!data.accessToken) return;
      localStorage.setItem("businessChooseToken", data.accessToken);
      getSelectableBusinesses.refetch();
    },
    onError: (error) => toast(showErrorToast(error), ErrorToastConfig),
  });

  const getSelectableBusinesses = useQuery<IBusinessResponse[]>({
    queryKey: ["selectable-businesses"],
    queryFn: BusinessService.getSelectableBusiness,
    enabled: false,
  });

  const chooseBusiness = useMutation({
    mutationFn: AuthService.chooseBusinessAfterLogin,
    onSuccess: ({ data }: { data: IAuth }) => {
      // Clean up tokens and storage
      localStorage.removeItem("businessChooseToken");
      localStorage.removeItem("mobile");
      localStorage.removeItem("method");

      if (data) {
        // Tracking
        const userIdentifier =
          data.account.email || data.account.phoneNumber || "unknown";
        TrackerService.identify(userIdentifier, mixpanelProfilePayload(data));
        TrackerService.track(
          TRACKER_CONSTANTS.LOGIN_SUCCESS.events.CompanyOptionsClicked,
          { ...session }
        );

        // Complete login flow
        login(data);
      }

      // Clean up recaptcha
      document.getElementById("recaptcha-container")?.remove();
      if (window.recaptchaVerifier) {
        delete (window as any).recaptchaVerifier;
      }

      // Refresh application state
      window.location.reload();
      // handleGoToNext();
    },
    onError: (error) => toast(showErrorToast(error), ErrorToastConfig),
  });

  const mutationSignInPhone = useMutation({
    mutationFn: AuthService.sendFirebaseOtp,
    onSuccess: (data) => {
      if (data?.status === 200) {
        localStorage.setItem("method", "signin-phone");
        handleGoToNext();
      }
    },
    onError: (error: any) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const mutationPhone = useMutation({
    mutationFn: AuthService.verifyPhoneNumber,
    onSuccess: ({ data }) => {
      if (!data.isRegisteredPhone) {
        localStorage.setItem("method", "signup-phone");
        setShowPhone(false);
        setShowPhoneCompleted(true);
        setShowShippingAddress(true);
      } else {
        mutationSignInPhone.mutate(phoneSigninPayload);
      }
      setIsLoading(false);
    },
    onError: (error: any) => {
      setIsLoading(false);
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  /**
   * useEffects
   */

  /**
   * initialize recaptcha
   */
  useEffect(() => {
    if (!window.recaptchaVerifier && !session) {
      // create element if not found
      let recaptchaContainer = document.getElementById("recaptcha-container");
      if (!recaptchaContainer) {
        recaptchaContainer = document.createElement("div");
        recaptchaContainer.id = "recaptcha-container";
        document.body.append(recaptchaContainer);
      }

      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        }
      );

      window.recaptchaVerifier.verify();
    }
  }, []);

  /**
   * trigger chooseBusiness mutation
   */
  useEffect(() => {
    if (getSelectableBusinesses.data) {
      const singleBusiness = getSelectableBusinesses.data[0];
      if (
        singleBusiness?.businessAccountType === "BUYER" &&
        getSelectableBusinesses.data.length === 1
      ) {
        chooseBusiness.mutate(singleBusiness.id);
      } else {
        setOpenBusinessSelectionModal(true);
      }
    }
  }, [getSelectableBusinesses.data]);

  /**
   * handler functions
   */

  /**
   * Sign up with phone
   * @param {IOtpVerificationPayload} payload
   * @returns {Promise<string>}
   */
  async function handleSignUpWithPhone(payload: IOtpVerificationPayload) {
    if (!window.confirmationResult) {
      console.error("Invalid or missing verificationId.");
      return;
    }

    try {
      localStorage.setItem("method", "signin-phone");
      const userCredential = await window.confirmationResult.confirm(
        payload.code
      );

      const data = getValues();

      const referralCode = localStorage.getItem("referral_code") || null;
      const referrerUserId = localStorage.getItem("referral_owner") || null;

      await AuthService.signUpWithPhone({
        uid: userCredential.user.uid,
        fullName: data.fullName,
        companyCountryCode: data.companyCountryCode,
        phoneNumber: data.phoneNumber,
        phoneNumberCountryCode: data.phoneNumberCountryCode,
        businessAccountType: "BUYER",
        address: data.address,
        ...(referralCode && { referralCode, referrerUserId }),
      });

      return await userCredential.user.getIdToken();
    } catch (e: any) {
      console.error("Error verifying OTP:", e);
      toast(e, ErrorToastConfig);
      setIsDisabled(false);
      throw e;
    }
  }

  async function handleVerifyFirebaseOtp(otp: string) {
    const confirmationResult = window.confirmationResult;

    if (!confirmationResult) {
      console.error("Invalid or missing verificationId.");

      navigate(`/signup?${searchParams.toString()}`);
      return;
    }
    try {
      const userCredential = await confirmationResult.confirm(otp);

      return await userCredential.user.getIdToken();
    } catch (e: any) {
      console.error("Error verifying OTP:", e);
      const errorMessage = i18next.t(`commons.error.form.invalidOtp`);
      toast(showErrorToast(errorMessage), ErrorToastConfig);
      setIsDisabled(false);
      throw e;
    }
  }

  /**
   * validate each step items before proceed
   * @param step number
   * @returns boolean
   */
  const handleValidateStep = (step: number) => {
    let result = false;

    // if session exist just return true for previous steps
    if (session && step < 3) {
      return true;
    }

    switch (step) {
      case 1:
        const isSignin = localStorage.getItem("method");
        if (isSignin) {
          result = true;
          break;
        }
        const fieldsToValidate = getValues([
          "phoneNumber",
          "phoneNumberCountryCode",
          "address.title",
          "address.fullName",
          "address.address",
          "address.countryCode",
          "address.province",
          "address.phoneNumber",
          "address.phoneNumberCountryCode",
        ]);

        // make sure all fields are filled
        const areFieldsValid =
          !fieldsToValidate.some((field) => field === undefined) &&
          !fieldsToValidate.includes(null) &&
          !fieldsToValidate.includes("");

        result = areFieldsValid;
        break;

      case 2:
        result = session ? true : false;
        break;

      case 3:
        if (catalog) {
          switch (catalog.paymentFlowType) {
            case PaymentFlowTypeEnum.FULL_BEFORE_DELIVERY:
              result = Boolean(getValues("paymentMethod"));
              break;
            case PaymentFlowTypeEnum.MULTIPLE_PAYMENTS:
              result = Boolean(getValues("paymentMethod"));
              break;
            case PaymentFlowTypeEnum.FULL_AFTER_DELIVERY:
              result = true;
              break;

            default:
              result = false;
              break;
          }
        }
        break;

      default:
        result = false;
        break;
    }

    return result;
  };

  /**
   * proceed to next step
   */
  const handleGoToNext = () => {
    if (!handleValidateStep(currentStep)) {
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  /**
   * go back to previous step
   */
  const handleGotoPrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  /**
   * Decrease seconds by 1 every second, until it reaches 0 and clearInterval is called
   */
  const handleCountDownInterval = () => {
    setSeconds((prevSeconds) => {
      if (prevSeconds - 1 <= 0) {
        clearInterval(timerRef.current);
        return 0;
      }
      return prevSeconds - 1;
    });
  };

  // const handleCountDownPhoneChangeInterval = () => {
  //   setChangePhoneSeconds((prevSeconds) => {
  //     if (prevSeconds - 1 <= 0) {
  //       clearInterval(timerRef.current);
  //       return 0;
  //     }
  //     return prevSeconds - 1;
  //   });
  // };

  /**
   * Resends OTP code to user's phone number.
   *
   * Internally, it calls AuthService.sendFirebaseOtp with user's phone number and
   * phone number country code. If the call fails, it logs the error to console.
   *
   * It also starts/resets the 60 second countdown timer and decrements the max tries
   * counter. If the max tries counter reaches 0, the "Resend OTP code" button is
   * disabled.
   */
  const handleResendOTPCode = async () => {
    const data = getValues();
    const sendFirebaseOtp = await AuthService.sendFirebaseOtp({
      phoneNumber: data.phoneNumber,
      phoneNumberCountryCode: data.phoneNumberCountryCode,
    });

    if (sendFirebaseOtp?.status !== 200) {
      console.error("Error resending OTP from firebase", sendFirebaseOtp);
    }

    setSeconds(RESEND_CODE_COUNTDOWN);
    setMaxTries((prev) => prev - 1);
    timerRef.current = setInterval(handleCountDownInterval, 1000);
  };

  const handleSubmitPhone = async () => {
    const phoneNumber = getValues("phoneNumber");
    const phoneNumberCountryCode = getValues("phoneNumberCountryCode");
    setValue("address.phoneNumber", phoneNumber);
    setValue("address.phoneNumberCountryCode", phoneNumberCountryCode);
    if (phoneNumber && phoneNumberCountryCode) {
      let payload = { phoneNumber, phoneNumberCountryCode };
      setIsLoading(true);
      setphoneSigninPayload(payload);
      mutationPhone.mutate(payload);
      setSearchParams({ newUser: "true" });
    }
  };

  const handleSubmitAddress = async () => {
    setIsLoading(true);
    console.log("SUBMIT ADDRESS", getValues());
    try {
      const data = getValues();
      setValue("fullName", data.address.fullName);
      setValue("companyCountryCode", data.address.countryCode);
      const sendOtpResult = await AuthService.sendFirebaseOtp({
        phoneNumber: data.phoneNumber!,
        phoneNumberCountryCode: data.phoneNumberCountryCode!,
      });

      if (sendOtpResult?.status === 200) {
        localStorage.setItem("mobile", data.phoneNumber!);
        localStorage.setItem("countryCode", data.phoneNumberCountryCode!);
      }
    } catch (error) {
      console.error("Error sending OTP", error);
    }
    handleGoToNext();
    setIsLoading(false);
  };

  const handleSubmitOTP = async () => {
    setIsLoading(true);
    console.log(getValues());
    const data = getValues();
    const verifyMethod = localStorage.getItem("method") || "signup-email";

    if (verifyMethod === "signin-phone") {
      const isOtpValid = await handleVerifyFirebaseOtp(otpInput);

      if (isOtpValid) signinSSOMutation.mutate({ accessToken: isOtpValid });
      else {
        const errorMessage = i18next.t(`commons.error.form.invalidOtp`);
        toast(showErrorToast(errorMessage), ErrorToastConfig);
      }

      return;
    }

    otpVerifyMutation.mutate({
      phoneNumber: data.phoneNumber,
      countryCode: data.phoneNumberCountryCode,
      code: otpInput,
      token: localStorage.getItem("phoneVerificationToken") || "",
    });
  };

  function closeBusinessModalHandler() {
    setOpenBusinessSelectionModal(false);
  }

  return {
    // handler functions
    handleValidateStep,
    handleGoToNext,
    handleGotoPrev,
    handleResendOTPCode,
    handleVerifyFirebaseOtp,
    handleSubmitPhone,
    handleSubmitAddress,
    handleSubmitOTP,
    // states
    currentStep,
    setCurrentStep,
    changePhoneSeconds,
    setChangePhoneSeconds,
    phoneNoChangeAttempts,
    setPhoneNoChangeAttempts,
    isDisabled,
    setIsDisabled,
    otpInput,
    setOtpInput,
    maxTries,
    setMaxTries,
    seconds,
    setSeconds,
    isLoading,
    setIsLoading,
    isValidPhone,
    setIsValidPhone,
    showPhoneCompleted,
    setShowPhoneCompleted,
    showPhone,
    setShowPhone,
    isValidAddress,
    setIsValidAddress,
    showShippingAddress,
    setShowShippingAddress,
    showSignIn,
    setShowSignIn,
    // mutations and variables
    otpVerifyMutation,
    signinSSOMutation,
    mutationPhone,
    isError: otpVerifyMutation.isError || signinSSOMutation.isError,
    // isLoading: otpVerifyMutation.isPending || signinSSOMutation.isPending,
    openBusinessSelectionModal,
    closeBusinessModalHandler,
  };
};

export default useFormContainer;
